import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular-boost';
import { Observable } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import { candidateNotesQuery } from '../graphql/queries/candidateNotes';
import {
  IAccount,
  INote,
  ICandidate,
  IApplicationClickInfo,
  IApplication,
} from '../interfaces/graph-types';

type ErrorCallback = (err: string) => any;
type SuccessCallback = (data: any) => any;

@Injectable()
export class CandidateService {
  // ------------------------------------------------------
  constructor(private apollo: Apollo) {}

  // ------------------------------------------------------
  public getCandidateInfo(
    accountId: string,
    candidateId: string,
  ): Observable<ICandidate> {
    return this.apollo
      .watchQuery<{ account: IAccount }>({
        variables: { accountId, candidateId },
        query: gql`
          query candidateProfile($accountId: ID!, $candidateId: ID!) {
            account(accountId: $accountId) {
              id
              candidate(candidateId: $candidateId) {
                id
                avatarUrl
                email
                firstName
                lastName
                name
                phone
                score
                mxProvider
                internetSpeed
                updatedAt
                createdAt
                source
                sourceInfo
                applications {
                  id
                  stageName
                  stage
                  error
                  stepCount
                  stepType
                  source
                  processAt
                  createdAt
                  funnel {
                    id
                    name
                  }
                }
                attributes {
                  id
                  attributeValueId
                  name
                  icon
                  value
                  placeholder
                }
                tags {
                  id
                  name
                  icon
                  value
                }
              }
            }
          }
        `,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(map(res => res.data.account.candidate));
  }

  // ------------------------------------------------------
  public getApplicationInfo(applicationId: string): Observable<IApplication> {
    return this.apollo
      .watchQuery<{ application: IApplication }>({
        variables: { applicationId },
        query: gql`
          query applicationProfile($applicationId: ID!) {
            application(applicationId: $applicationId) {
              id
              applicationStepInfos {
                id
                response
                createdAt
                updatedAt
                funnelStepVariation {
                  id
                  type
                  required
                  statement
                  descriptionHtml
                  minSelection
                  maxSelection
                  choices
                  minScore
                  manualReview
                  explanation
                  funnelStep {
                    id
                    position
                  }
                }
              }
            }
          }
        `,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(map(res => res.data.application));
  }

  // ------------------------------------------------------
  public setAsLead(
    candidateIds: string[],
    isLead: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateIds: candidateIds,
      isLead: isLead,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation setCandidatesAsLead($input: SeCandidatesAsLeadInput!) {
            setCandidatesAsLead(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setCandidatesAsLead;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteNote(
    candidateId: string,
    noteId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateId,
      noteId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteCandidateNote($input: DeleteCandidateNoteInput!) {
            deleteCandidateNote(input: $input) {
              error
            }
          }
        `,
        refetchQueries: [
          {
            query: candidateNotesQuery,
            variables: { candidateId },
          },
        ],
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteCandidateNote;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public doNotContactCandidates(
    candidateIds: string[],
    doNotContact: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateIds: candidateIds,
      doNotContact: doNotContact,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation setCandidatesAsDoNotContact(
            $input: SetCandidatesAsDoNotContactInput!
          ) {
            setCandidatesAsDoNotContact(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setCandidatesAsDoNotContact;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public cancelCandidates(
    accountId: string,
    candidateIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      candidateIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation cancelCandidates($input: CanceCandidatesInput!) {
            cancelCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].cancelCandidates;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public ExportCandidates(
    accountId: string,
    candidateIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      candidateIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation exportCandidates($input: ExportCandidatesInput!) {
            exportCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].exportCandidates;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public verifyEmails(
    candidateIds: string[],
    selectedAddon: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateIds,
      selectedAddon,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation verifyCandidates($input: VerifCandidatesInput!) {
            verifyCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].verifyCandidates;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public assignCandidatesToGroup(
    candidateIds: string[],
    groupId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateIds,
      groupId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation assignCandidatesToGroup(
            $input: AssignCandidatesToGroupInput!
          ) {
            assignCandidatesToGroup(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].assignCandidatesToGroup;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteCandidates(
    candidateIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteCandidates($input: DeleteCandidatesInput!) {
            deleteCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteCandidates;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteApplications(
    applicationIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      applicationIds: applicationIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteApplications($input: DeleteApplicationsInput!) {
            deleteApplications(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteApplications;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public cancelApplications(
    applicationIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      applicationIds: applicationIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation cancelApplications($input: CancelApplicationsInput!) {
            cancelApplications(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].cancelApplications;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public resumeApplications(
    applicationIds: string[],
    processAt: number,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      applicationIds: applicationIds,
      processAt,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation resumeApplications($input: ResumeApplicationsInput!) {
            resumeApplications(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].resumeApplications;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setLive(
    funnelId: number,
    live: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId: funnelId,
      live: live,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setFunnelLive($input: SetFunnelLiveInput!) {
            setFunnelLive(input: $input) {
              live
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        if (successfunction) {
          successfunction(result['data'].setFunnelLive);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public notes(candidateId: string): Observable<INote[]> {
    return this.apollo
      .watchQuery<any>({
        variables: { candidateId },
        query: candidateNotesQuery,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(
        map(({ data }) => data.candidate.notes),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public addNote(
    candidateId: string,
    content: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateId,
      content,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation addCandidateNote($input: AddCandidateNoteInput!) {
            addCandidateNote(input: $input) {
              error
            }
          }
        `,
        refetchQueries: [
          {
            query: candidateNotesQuery,
            variables: { candidateId },
          },
        ],
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].addCandidateNote;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setTag(
    accountId: string,
    candidateId: string,
    tagId: string,
    value: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = { candidateId, tagId, value };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setCandidateTag($input: SetCandidateTagInput!) {
            setCandidateTag(input: $input) {
              error
              value
            }
          }
        `,
        variables: { input: input },

        update: (store, results) => {
          // { data: { setCandidateTag } }
          // We don't want to update the cache if result is an error

          if (results['data']['setCandidateTag']['error']) {
            return;
          }

          // Read the data from our cache for this query.
          let query = gql`
            query updateTagState($accountId: ID!, $candidateId: ID!) {
              account(accountId: $accountId) {
                candidate(candidateId: $candidateId) {
                  tags {
                    id
                    value
                  }
                }
              }
            }
          `;
          const cache = store.readQuery({
            query: query,
            variables: { accountId, candidateId },
          });
          // update our flag status
          let tags = cache['account']['candidate']['tags'];
          tags.forEach(tag => {
            if (tag.id == tagId) {
              tag.value = value;
            }
          });
          // Write our data back to the cache.
          store.writeQuery({ query: query, data: cache });
        },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setCandidateTag;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setField(
    candidateId: string,
    fieldName: string,
    value: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateId,
      fieldName,
      value,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setCandidateField($input: SetCandidateFieldInput!) {
            setCandidateField(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setCandidateField;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setAttributeField(
    candidateId: string,
    candidateAttributeId: string,
    candidateAttributeValueId: string,
    fieldName: string,
    value: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      candidateId: candidateId,
      candidateAttributeId: candidateAttributeId,
      candidateAttributeValueId: candidateAttributeValueId,
      fieldName: fieldName,
      value: value,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setCandidateAttributeValueField(
            $input: SetCandidateAttributeValueFieldInput!
          ) {
            setCandidateAttributeValueField(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setCandidateAttributeValueField;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public applyTagsToCandidates(
    accountId: string,
    addTagIds: string[],
    removeTagIds: string[],
    candidateIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      addTagIds,
      removeTagIds,
      candidateIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation applyTagsToCandidates($input: ApplyTagsTCandidatesInput!) {
            applyTagsToCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].applyTagsToCandidates;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkApplyTagsToCandidates(
    accountId: string,
    addTagIds: string[],
    removeTagIds: string[],
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      addTagIds,
      removeTagIds,
      searchFilter: JSON.stringify(searchFilter),
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkApplyTagsToCandidates(
            $input: BulkApplyTagsToCandidatesInput!
          ) {
            bulkApplyTagsToCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].bulkApplyTagsToCandidates;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }
}
